import { useEffect, useState } from 'react';
import Button from '../../components/button/Button';
import LinkButton from '../../components/button/LinkButton';
import ScreenContainer from '../../components/layout/ScreenContainer';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../../FirebaseConfig';
import PhoneInput from 'react-phone-input-2';
import Loader from '../../components/loader/Loader';
import { SignupAPI } from '../../logic/signupApi/signupApi';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { useCountry, formatPhoneNumber } from '../../utils/phoneNumberUtils';
import { getOrCreateRecaptchaVerifier } from '../../utils/recaptchaUtils';

const PhoneScreen = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { handleSubmit } = useForm();

  const [linkRef, setLinkRef] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [errMsgVisible, setErrMsgVisible] = useState(false);
  const [errMsgTxt, setErrMsgTxt] = useState('');

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic validation: phone should have at least 10 digits (country code + number)
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  const onSubmitSMS = async (data: any) => {
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      setErrMsgTxt(t('INVALID_PHONE_NUMBER'));
      setErrMsgVisible(true);
      return;
    }

    setIsLoading(true);

    // Format phone number (ensures single + prefix)
    const formattedPhone = formatPhoneNumber(phoneNumber);

    // Get or create recaptcha verifier
    let appVerifier: RecaptchaVerifier;
    try {
      appVerifier = getOrCreateRecaptchaVerifier('recaptcha-container');
    } catch (error: any) {
      setIsLoading(false);
      setErrMsgTxt(error.message || t('VERIFICATION_SYSTEM_NOT_READY'));
      setErrMsgVisible(true);
      return;
    }

    await SignupAPI.checkIfPhoneExists(formattedPhone).then(async (response) => {
      const userEmail = response.email || email;
      signInWithPhoneNumber(auth, formattedPhone, appVerifier)
        .then((confirmationResult) => {
          setIsLoading(false);
          navigate(`/login/verification${linkRef ? `?ref=${linkRef}` : ''}`, {
            state: {
              email: userEmail,
              phone: formattedPhone, // Use formattedPhone to avoid double plus
              phoneConfirmation: JSON.stringify(confirmationResult),
              verificationMethod: 'sms'
            }
          });
        }).catch((error) => {
          console.log("SMS Error: ", error);
          setErrMsgTxt(t('PHONE_LOGIN_GENERAL_ERROR'));
          setErrMsgVisible(true);
          setIsLoading(false);
        });
    }).catch((error: any) => {
      if (error?.data?.message === 'multi-factor-auth-required') {
        setIsLoading(false);
        setErrMsgTxt(t('PHONE_LOGIN_DISABLED_2FA'));
        setErrMsgVisible(true);
        return;
      }
      else if (error?.data?.message === 'no-phone-found') {
        setIsLoading(false);
        navigate(`/signup${linkRef ? `?ref=${linkRef}` : ''}`, { state: { phone: phoneNumber } });
      }
      else {
        //fallback
        setIsLoading(false);
        setErrMsgTxt(JSON.stringify(error));
        setErrMsgVisible(true);
      }
    })
  };

  const onSubmitVoiceCall = async () => {
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      setErrMsgTxt(t('INVALID_PHONE_NUMBER'));
      setErrMsgVisible(true);
      return;
    }

    setIsLoading(true);

    // Format phone number (ensures single + prefix)
    const formattedPhone = formatPhoneNumber(phoneNumber);

    await SignupAPI.checkIfPhoneExists(formattedPhone).then(async (response: any) => {
      const userEmail = response.email || email;

      if (!userEmail) {
        setIsLoading(false);
        setErrMsgTxt(t('COULD_NOT_RETRIEVE_EMAIL_FOR_PHONE'));
        setErrMsgVisible(true);
        return;
      }

      try {
        await SignupAPI.sendLoginCode({
          whereToSendTheCode: 'voice',
          email: userEmail,
          telephoneMobile: formattedPhone,
          language: i18n.language
        });
        setIsLoading(false);
        navigate(`/login/verification${linkRef ? `?ref=${linkRef}` : ''}`, {
          state: {
            email: userEmail,
            phone: formattedPhone, // Use formattedPhone to avoid double plus
            verificationMethod: 'voice'
          }
        });
      } catch (error: any) {
        setIsLoading(false);
        setErrMsgTxt(t('COULD_NOT_SEND_VOICE_CALL_RETRY_TEXT'));
        setErrMsgVisible(true);
      }
    }).catch((error: any) => {
      if (error?.data?.message === 'multi-factor-auth-required') {
        setIsLoading(false);
        setErrMsgTxt(t('PHONE_LOGIN_DISABLED_2FA'));
        setErrMsgVisible(true);
        return;
      }
      else if (error?.data?.message === 'no-phone-found') {
        setIsLoading(false);
        navigate(`/signup${linkRef ? `?ref=${linkRef}` : ''}`, { state: { phone: phoneNumber } });
      }
      else {
        //fallback
        setIsLoading(false);
        setErrMsgTxt(JSON.stringify(error));
        setErrMsgVisible(true);
      }
    });
  };

  const [phoneNumber, setPhoneNumber] = useState('');

  const email = useLocation().state?.email || null;
  const filledPhone = useLocation().state?.filledPhone || null;
  const phone = useLocation().state?.phone || null;

  const { country } = useCountry();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (phone || filledPhone) {
      setPhoneNumber(phone || filledPhone);
    }

    const link = urlParams.get('ref');
    if (link) {
      setLinkRef(link);
    }
  }, []);

  useEffect(() => {
    const initializeRecaptcha = () => {
      try {
        getOrCreateRecaptchaVerifier('recaptcha-container');
      } catch (error) {
        console.error('Error initializing RecaptchaVerifier:', error);
      }
    };

    const timeoutId = setTimeout(initializeRecaptcha, 100);

    return () => {
      clearTimeout(timeoutId);
      if (window.recaptchaVerifier && !(window.recaptchaVerifier as any).destroyed) {
        try {
          window.recaptchaVerifier?.clear();
        } catch (e) {
          console.error("Cleanup error", e);
        }
      }
    };
  }, []);


  return (
    <ScreenContainer pageTitle={t('PAGE_TITLE_LOGIN_PHONE')}>
        {
          errMsgVisible && <ErrorMessage message={errMsgTxt} onClose={() => { setErrMsgVisible(false); }} />
        }
        <h2 className='size-h2 u-mb-1 u-text-bold'>{t('WELCOME')}!</h2>
        <h3 className='size-h3 u-mb-2 u-text-bold'>
          {
            phone ? t('PHONE_SCREEN_TEXT_FIRST') : t('PHONE_SCREEN_TEXT_SECOND')
          }
        </h3>
        <form onSubmit={handleSubmit(onSubmitSMS)}>
          <div className='form-group u-mb-2'>
            {
              // If country is assigned and there is a phone value in the field the value is removed for some reason
              phone ?
                // if we come to this screen from the registration with a phone number,
                // the country is automatically detected from the phone number,
                // so we can bypass the bug by not fetching the country
                <PhoneInput
                  value={phoneNumber}
                  inputClass={'phone-input form-control-lg'}
                  buttonClass={'phone-button'}
                  onChange={(phone) => {
                    setPhoneNumber(phone)
                  }} />
                :
                // if we come here without a phone number to display,
                // we fetch and assign the country
                // and since there is no value that is going to disappear its okay
                <PhoneInput
                  country={country}
                  inputClass={'phone-input form-control-lg'}
                  buttonClass={'phone-button'}
                  value={phoneNumber}
                  onChange={(phone) => {
                    setPhoneNumber(phone)
                  }} />
            }
          </div>
          {isLoading && <Loader />}
          <div id="recaptcha-container"></div>
          <Button title={t('TEXT_ME_LOGIN_CODE')} />
          <Button
            secondary
            title={t('I_PREFER_A_CALL')}
            onClick={onSubmitVoiceCall}
            disabled={isLoading}
          />
        </form>
        <LinkButton
          title={t('PHOHE_SCREEN_TEXT_THIRD')}
          onClick={() => navigate(`/login/${linkRef ? `?ref=${linkRef}` : ''}`, { state: { email: email, phone: phoneNumber } })}
        />
    </ScreenContainer>
  )
};

export default PhoneScreen